import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Divider, IconButton, lighten, ListItem, ListItemButton, ListItemText, Tooltip, Typography, } from '@mui/material';
import { Box } from '@mui/system';
import { preventDefault, useBox } from 'shared/hooks/utils';
import { Author } from 'z3k-config';
import { unpack_validation } from 'z3k-parser/validation/types';
import { useCallback, useContext, useMemo, useState, } from 'react';
import { Z3KSettings, Z3KSettingsEditor } from '../../../provider/types';
import { isAuthor } from 'z3k-parser/model/author';
import { Add, Delete } from '@mui/icons-material';
import { useListEditingCallbacks } from './config';
import { useLocation, useNavigate } from 'react-router';
import DndIcon from 'console/device-settings/z3k/images/dnd.svg';
import { MessageBox } from 'shared/components/dialogs/message-box';
import { MaterialHelpPopupUncontrolled } from '../../material-utils/help-popup';
import { getItemGroupFromPath } from '../../../layout/drawer/path-utils';
import { SortableList } from 'console/shared/components/features/sortable-list';
import { isEqual } from 'lodash';
import { useBooleanState } from 'shared/hooks/state/boolean';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
export function DragDropDrawer(props) {
    const navigate = useNavigate();
    const location = useLocation();
    const { service_view, device_spec } = useContext(Z3KSettings);
    const { config } = useContext(Z3KSettingsEditor);
    const { validation_result: list_validation_result, validation_warnings_result: list_warning_validation_result, } = useContext(Z3KSettingsEditor);
    const [msgs, setMsgs] = useState([]);
    const [going_to_delete_object, setGoingToDeleteObject] = useState(null);
    const { enableDnd, help_id, selected_item_id, mainTitleComponentRender, objectFilter, beforeTitleComponentRender, nameGetter, titleComponentRender, detailsComponentRender, is_climatic_hc, } = props;
    const { value: list_of_objects, validation_result, validation_warnings_result, onAddClick, onCopyClick, onDelete, onChange, onConfirmDelete, } = useListEditingCallbacks(props.z3k_name);
    const onDeleteFunc = props.onDelete ?? onDelete;
    const onClickAddFunc = props.onAddClick ?? onAddClick;
    const onConfirmDeleteFunc = props.onConfirmDelete ?? onConfirmDelete;
    const boxed_list_of_objects = useBox(list_of_objects);
    const [dialog_open, closeDialog, openDialog] = useBooleanState(false);
    const onClickAddButton = useCallback(() => {
        onClickAddFunc?.();
    }, [onClickAddFunc]);
    const onClickCopyButton = useCallback((object, ev) => {
        preventDefault(ev);
        const copy_id = onCopyClick(object);
        const group_key = location?.state?.group_key ?? getItemGroupFromPath(location.pathname);
        navigate(`${props.item_url ?? props.z3k_name}`, {
            state: {
                group_key: group_key,
                item_key: props.item_url ?? props.z3k_name,
                item_id: copy_id,
            },
        });
    }, [onCopyClick]);
    const hideAddButton = useMemo(() => props.hideAddButton ?? !service_view, [props.hideAddButton, service_view]);
    const compareFn = useCallback(([a], [b]) => {
        const ap = 'position' in a ? a.position ?? 0 : 0, bp = 'position' in b ? b.position ?? 0 : 0;
        if (ap === 0 && bp === 0)
            return 0;
        if (ap === 0)
            return 1;
        if (bp === 0)
            return -1;
        return ap - bp;
    }, []);
    const onDeleteObject = useCallback((object) => {
        const group_key = location?.state?.group_key ?? getItemGroupFromPath(location.pathname);
        const new_list = boxed_list_of_objects.current.filter((obj) => obj.id !== object.id);
        if (onDeleteFunc != null) {
            onDeleteFunc(object, new_list);
        }
        else {
            onChange(new_list);
        }
        navigate('', {
            state: { group_key: group_key, item_key: null, item_id: null },
        });
    }, [onDeleteFunc, onChange]);
    const onClickCloseDialog = useCallback(() => {
        closeDialog();
        setGoingToDeleteObject(null);
    }, [closeDialog, setGoingToDeleteObject]);
    const onClickDeleteDialog = useCallback(() => {
        onClickCloseDialog();
        if (going_to_delete_object != null) {
            onDeleteObject(going_to_delete_object);
        }
    }, [onClickCloseDialog, onDeleteObject, going_to_delete_object]);
    const onTrashClick = useCallback((object) => (ev) => {
        preventDefault(ev);
        const new_list = boxed_list_of_objects.current.filter((obj) => obj.id !== object.id);
        const error_msgs = onConfirmDeleteFunc?.(object, new_list) ?? null;
        if (error_msgs && error_msgs.length > 0) {
            openDialog();
            setMsgs(error_msgs);
            setGoingToDeleteObject(object);
        }
        else {
            onDeleteObject(object);
        }
    }, [
        onConfirmDeleteFunc,
        setMsgs,
        setGoingToDeleteObject,
        onDeleteObject,
        openDialog,
    ]);
    const showTrashButton = useCallback((object) => {
        if (props.hideTrashButton ?? !service_view) {
            return false;
        }
        return !isAuthor(object.id, Author.PREDEFINED);
    }, [props.hideTrashButton, service_view]);
    const onClickItem = useCallback((id) => {
        const group_key = location?.state?.group_key ?? getItemGroupFromPath(location.pathname);
        navigate(`${props.item_url ?? props.z3k_name}`, {
            state: {
                group_key: group_key,
                item_key: props.item_url ?? props.z3k_name,
                item_id: id,
            },
        });
    }, []);
    const panels = useMemo(() => {
        return (list_of_objects ?? [])
            .map((object, index) => [object, index])
            .sort(compareFn)
            .flatMap(([object, index]) => {
            if (objectFilter != null ? !objectFilter(object) : false) {
                return [];
            }
            const id = object.id.toString();
            const _validation_result = unpack_validation(validation_result, index);
            const _validation_warnings_result = unpack_validation(validation_warnings_result, index);
            const getClimaticError = (heating_index) => {
                const zones_indexs = config.heating_modes?.reduce((acc, current, index) => {
                    const zone_index = current.heating_zones.findIndex((zone) => zone.heating_circuit ===
                        config.heating_circuits?.[heating_index].id);
                    if (zone_index !== -1) {
                        return [...acc, [index, zone_index]];
                    }
                    return acc;
                }, []);
                const threeway_tap_index = config.threeway_taps?.findIndex((item) => {
                    return config.heating_circuits?.[heating_index].control_devices.includes(item.id);
                });
                const pump_index = config.pumps?.findIndex((item) => {
                    return config.heating_circuits?.[heating_index].control_devices.includes(item.id);
                });
                const threeway_taps_validation = threeway_tap_index
                    ? unpack_validation(list_validation_result, 'threeway_taps', threeway_tap_index)
                    : null;
                const pumps_validation = pump_index
                    ? unpack_validation(list_validation_result, 'pumps', pump_index)
                    : null;
                const zones_validation = zones_indexs && zones_indexs.length > 0
                    ? zones_indexs.some((zones_index) => {
                        const validation = unpack_validation(list_validation_result, 'heating_modes', zones_index[0], 'heating_zones', zones_index[1]);
                        return !!validation;
                    })
                    : null;
                return !!(unpack_validation(list_validation_result, 'heating_circuits', index) ||
                    threeway_taps_validation ||
                    pumps_validation ||
                    zones_validation);
            };
            const has_error = is_climatic_hc
                ? getClimaticError(index)
                : _validation_result != null;
            const has_warning = is_climatic_hc
                ? null
                : _validation_result == null && _validation_warnings_result != null;
            return {
                id,
                component: (_jsx(ListItem, { disablePadding: true, divider: true, sx: (theme) => ({
                        transitionProperty: 'background-color, color',
                        ...(has_warning && {
                            background: theme.palette.warning.light,
                            color: theme.palette.warning.contrastText,
                            '&:hover': {
                                background: lighten(theme.palette.warning.light, 0.2),
                            },
                            '& .MuiTypography-body2': {
                                color: lighten(theme.palette.warning.contrastText, 0.2),
                            },
                        }),
                        ...(has_error && {
                            background: theme.palette.error.light,
                            color: theme.palette.error.contrastText,
                            '& .MuiTypography-colorTextSecondary': {
                                color: theme.palette.error.contrastText,
                            },
                            '&:hover': {
                                background: lighten(theme.palette.error.light, 0.2),
                            },
                        }),
                    }), onClick: () => onClickItem(object.id), children: _jsxs(ListItemButton, { selected: selected_item_id == object.id ? true : undefined, children: [enableDnd && _jsx(DndIcon, { className: "material-dnd" }), _jsx(ListItemText, { primary: _jsxs(Box, { display: "flex", alignItems: "center", justifyContent: "space-between", rowGap: 1, flexWrap: "wrap", children: [beforeTitleComponentRender &&
                                            beforeTitleComponentRender(object), _jsxs(Box, { flexGrow: 1, whiteSpace: "normal", children: [_jsx(Typography, { variant: "subtitle1", children: nameGetter != null
                                                        ? nameGetter(object, index)
                                                        : // @ts-ignore
                                                            object.name ?? '' }), _validation_result != null &&
                                                    typeof _validation_result ===
                                                        'string' && (_jsx(Typography, { variant: "subtitle2", children: _validation_result })), _validation_warnings_result != null &&
                                                    typeof _validation_warnings_result ===
                                                        'string' && (_jsx(Typography, { variant: "subtitle2", children: _validation_warnings_result }))] }), titleComponentRender &&
                                            titleComponentRender(object, selected_item_id == object.id)] }) }), !is_climatic_hc &&
                                service_view &&
                                props.z3k_name !== 'radiomodules' &&
                                props.z3k_name !== 'radiosensors' &&
                                props.z3k_name !== 'radiosensors433' &&
                                !(props.z3k_name === 'relay_controls' &&
                                    device_spec.io.has_exactly_one_relay_control) && (_jsx(Tooltip, { title: "\u041A\u043E\u043F\u0438\u044F", children: _jsx(Box, { children: _jsx(IconButton, { onClick: (ev) => onClickCopyButton(object, ev), size: "small", children: _jsx(ContentCopyIcon, { color: "action", sx: { fontSize: '1.2em' } }) }) }) })), service_view &&
                                detailsComponentRender &&
                                detailsComponentRender(object), (showTrashButton?.(object) ?? true) && (_jsx(Box, { children: _jsx(IconButton, { onClick: onTrashClick(object), size: "small", children: _jsx(Delete, {}) }) }))] }) }, ~~id + 1000)),
            };
        });
    }, [
        selected_item_id,
        list_of_objects,
        compareFn,
        objectFilter,
        validation_result,
        validation_warnings_result,
        beforeTitleComponentRender,
        nameGetter,
        titleComponentRender,
    ]);
    const boxedPanels = useBox(panels);
    const onSetList = useCallback((new_list, sortable) => {
        //need for not apply external updates.
        // We update objects only for drag
        if (sortable == null) {
            return;
        }
        if (isEqual(new_list, boxedPanels.current)) {
            return;
        }
        const right_position_ids = new Map(new_list.map(({ id }, position) => [Number(id), position]));
        const filtered_items = objectFilter
            ? boxed_list_of_objects.current.filter(objectFilter)
            : boxed_list_of_objects.current;
        const hidden_items = objectFilter
            ? boxed_list_of_objects.current.filter((item) => !objectFilter(item))
            : [];
        const result = [
            ...filtered_items.map((item, index) => ({
                ...item,
                position: (right_position_ids.get(item.id) ?? index) + 1,
            })),
            ...hidden_items.map((val, index) => ({
                ...val,
                position: filtered_items.length + index + 1,
            })),
        ];
        onChange(result);
    }, [objectFilter, onChange]);
    const _validation_result = unpack_validation(list_validation_result, props.z3k_name);
    const _validation_warnings_result = unpack_validation(list_warning_validation_result, props.z3k_name);
    const getClimaticError = () => {
        return !!(unpack_validation(list_validation_result, 'heating_circuits') ||
            unpack_validation(list_validation_result, 'heating_modes') ||
            unpack_validation(list_validation_result, 'threeway_taps') ||
            unpack_validation(list_validation_result, 'pumps'));
    };
    const has_error = is_climatic_hc ? getClimaticError() : _validation_result != null;
    const has_warning = is_climatic_hc
        ? null
        : _validation_result == null && _validation_warnings_result != null;
    return (_jsxs(Box, { overflow: "auto", children: [_jsx(Divider, {}), props.name && (_jsxs(_Fragment, { children: [_jsxs(Box, { width: "100%", display: "flex", flexDirection: "column", justifyContent: "center", p: 1, overflow: "hidden", textAlign: "center", whiteSpace: "normal", sx: (theme) => ({
                            transitionProperty: 'background-color, color',
                            ...(has_warning && {
                                background: theme.palette.warning.light,
                                color: theme.palette.warning.contrastText,
                                '&:hover': {
                                    background: lighten(theme.palette.warning.light, 0.2),
                                },
                                '& .MuiTypography-body2': {
                                    color: lighten(theme.palette.warning.contrastText, 0.2),
                                },
                            }),
                            ...(has_error && {
                                background: theme.palette.error.light,
                                color: theme.palette.error.contrastText,
                                '& .MuiTypography-colorTextSecondary': {
                                    color: theme.palette.error.contrastText,
                                },
                                '&:hover': {
                                    background: lighten(theme.palette.error.light, 0.2),
                                },
                            }),
                        }), children: [_jsxs(Box, { display: "flex", gap: 1, textAlign: "center", justifyContent: "center", children: [_jsx(Typography, { variant: "h6", color: "secondary.main", children: props.name }), help_id && (_jsx(MaterialHelpPopupUncontrolled, { help_id: help_id, help_options: props?.help_options }))] }), _validation_result != null &&
                                typeof _validation_result === 'string' && (_jsx(Typography, { variant: "subtitle2", children: _validation_result })), _validation_warnings_result != null &&
                                typeof _validation_warnings_result === 'string' && (_jsx(Typography, { variant: "subtitle2", children: _validation_warnings_result }))] }), _jsx(Divider, {})] })), mainTitleComponentRender && mainTitleComponentRender(), panels.length > 0 ? (_jsx(SortableList, { list: panels, setList: onSetList, disabled: !enableDnd, ghostClass: "sortable-list-ghost" })) : (_jsx(Box, { width: "100%", display: "flex", alignItems: "center", justifyContent: "center", py: 1, children: _jsx(Typography, { variant: "subtitle2", color: "default", children: "\u041D\u0435\u0442 \u0434\u043E\u0441\u0442\u0443\u043F\u043D\u044B\u0445 \u044D\u043B\u0435\u043C\u0435\u043D\u0442\u043E\u0432" }) })), !hideAddButton && (_jsx(Box, { display: "flex", justifyContent: "center", alignItems: "center", py: 1, children: _jsx(Button, { color: "primary", variant: "outlined", onClick: onClickAddButton, startIcon: _jsx(Add, {}), children: "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C" }) })), _jsx(MessageBox, { open: dialog_open, onClose: onClickCloseDialog, title: "\u041F\u0440\u0435\u0434\u0443\u043F\u0440\u0435\u0436\u0434\u0435\u043D\u0438\u0435", maxWidth: "xs", isWarning: true, footer: _jsxs(_Fragment, { children: [_jsx(Button, { color: "primary", onClick: onClickCloseDialog, children: "\u041E\u0442\u043C\u0435\u043D\u0430" }), _jsx(Button, { color: "error", onClick: onClickDeleteDialog, children: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C" })] }), children: msgs.map((msg, index) => (_jsxs(Box, { children: [index > 0 && _jsx(Divider, {}), _jsx(Typography, { variant: "subtitle1", color: "textSecondary", children: msg })] }, index))) })] }));
}
